import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Stack,
  Button,
  FormControl,
  InputLabel,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { TablePagination } from "@mui/material";
import {
  getExpedientesPorMes,
  actualizarColorExpediente,
} from "../services/expedientes.service";
import ExpedienteDetalleModal from "../expedientes/ExpedienteDetalleModal";
import { Expediente } from "../types/expedientes";

/* ========================= */
const MESES = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" }, { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" }, { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" },
];

const DISTRITOS = [
  { value: "01", label: "AMAZONAS" },
  { value: "02", label: "ANCASH" },
  { value: "03", label: "APURIMAC" },
  { value: "04", label: "AREQUIPA" },
  { value: "05", label: "AYACUCHO" },
  { value: "06", label: "CAJAMARCA" },
  { value: "07", label: "CALLAO" },
  { value: "08", label: "CAÑETE" },
  { value: "50", label: "CORTE SUPREMA PENAL" },
  { value: "10", label: "CUSCO" },
  { value: "25", label: "DEL SANTA" },
  { value: "11", label: "HUANCAVELICA" },
  { value: "12", label: "HUANUCO" },
  { value: "13", label: "HUAURA" },
  { value: "14", label: "ICA" },
  { value: "15", label: "JUNIN" },
  { value: "16", label: "LA LIBERTAD" },
  { value: "17", label: "LAMBAYEQUE" },
  { value: "18", label: "LIMA" },
  { value: "32", label: "LIMA ESTE" },
  { value: "09", label: "LIMA NORTE" },
  { value: "30", label: "LIMA SUR" },
  { value: "19", label: "LORETO" },
  { value: "27", label: "MADRE DE DIOS" },
  { value: "28", label: "MOQUEGUA" },
  { value: "29", label: "PASCO" },
  { value: "20", label: "PIURA" },
  { value: "33", label: "PUENTE PIEDRA" },
  { value: "21", label: "PUNO" },
  { value: "22", label: "SAN MARTIN" },
  { value: "34", label: "SELVA CENTRAL" },
  { value: "31", label: "SULLANA" },
  { value: "23", label: "TACNA" },
  { value: "26", label: "TUMBES" },
  { value: "24", label: "UCAYALI" },
];

/* 🔥 MAPEO DE COLORES */
const colorMap: Record<number, string> = {
  0: "#ffffff",
  1: "#ff4d4f",
  2: "#faad14",
  3: "#52c41a",
  4: "#1890ff",
};

/* ========================= */
const DashboardPage = () => {
  const navigate = useNavigate();
  const now = new Date();

  const bottomRef = useRef<HTMLDivElement>(null);

  const [materiaFiltro, setMateriaFiltro] = useState<string>("ALL");

  // 🔥 AHORA SÍ ES DINÁMICO
  const [anio, setAnio] = useState(now.getFullYear());

  const [mes, setMes] = useState(now.getMonth() + 1);
  const [materia] = useState("CI");
  const [distrito, setDistrito] = useState("16");

  const [rows, setRows] = useState<Expediente[]>([]);
  const [loading, setLoading] = useState(false);

  const [openDetalle, setOpenDetalle] = useState(false);
  const [selectedExpediente, setSelectedExpediente] =
    useState<Expediente | null>(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const materiasUnicas = Array.from(
    new Set(rows.map((r) => r.materia))
  ).filter(Boolean);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleUpdateRow = (updated: Expediente) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === updated.id ? { ...row, ...updated } : row
      )
    );
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const resp = await getExpedientesPorMes(anio, mes, materia, distrito);
        const data = resp.data.map((item: Expediente) => ({
          ...item,
          color: item.color ?? null,
        }));
        setRows(data);
      } catch (err) {
        console.error(err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [anio, mes, materia, distrito]);

  const handleVerDetalle = (exp: Expediente) => {
    setSelectedExpediente(exp);
    setOpenDetalle(true);
  };

  /* =========================
  COLUMNS
  ========================= */
  const columns: GridColDef[] = [
    { field: "nro_expediente", headerName: "Expediente", flex: 1.5 },
    { field: "organo_jurisdiccional", headerName: "Órgano", flex: 1.6 },
    { field: "tipo_expediente", headerName: "Tipo", flex: 1.1 },
    { field: "materia", headerName: "Materia", flex: 1 },
    { field: "estado", headerName: "Estado", flex: 1 },
    {
      field: "telefono",
      headerName: "Teléfono",
      flex: 1,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "color",
      headerName: "Estado",
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const setColor = async (color: number) => {
          setRows((prev) =>
            prev.map((row) =>
              row.id === params.row.id ? { ...row, color } : row
            )
          );
          try {
            await actualizarColorExpediente(params.row.id, color);
          } catch (err) {
            console.error(err);
          }
        };

        return (
          <Stack direction="row" spacing={1}>
            <Box onClick={() => setColor(0)} sx={circle("#ffffff")} />
            <Box onClick={() => setColor(1)} sx={circle("#ff4d4f")} />
            <Box onClick={() => setColor(2)} sx={circle("#faad14")} />
            <Box onClick={() => setColor(3)} sx={circle("#52c41a")} />
            <Box onClick={() => setColor(4)} sx={circle("#1890ff")} />
          </Stack>
        );
      },
    },
    {
      field: "acciones",
      headerName: "",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <VisibilityIcon
          sx={{ cursor: "pointer" }}
          onClick={() => handleVerDetalle(params.row)}
        />
      ),
    },
  ];

  const rowsFiltrados =
    materiaFiltro === "ALL"
      ? rows
      : rows.filter((r) => r.materia === materiaFiltro);

  return (
    <Box p={3}>
      {/* ── HEADER ── */}
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Casos recientes</Typography>

        <Stack direction="row" spacing={2}>
          {/* 🔥 AÑO HABILITADO */}
          <FormControl size="small">
            <InputLabel>Año</InputLabel>
            <Select
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
            >
              {Array.from({ length: now.getFullYear() - 2021 }, (_, i) => {
                const year = now.getFullYear() - i;
                return (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Mes</InputLabel>
            <Select
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
            >
              {MESES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" disabled>
            <InputLabel>Especialidad</InputLabel>
            <Select value={materia}>
              <MenuItem value="CI">CIVIL</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" disabled>
            <InputLabel>Distrito</InputLabel>
            <Select value={distrito}>
              {DISTRITOS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <InputLabel>Materia</InputLabel>
            <Select
              value={materiaFiltro}
              onChange={(e) => setMateriaFiltro(e.target.value)}
            >
              <MenuItem value="ALL">Todas</MenuItem>
              {materiasUnicas.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>



          <Button variant="contained" color="error" onClick={handleLogout}>
            Logout
          </Button>
        </Stack>
      </Stack>
<Box
  display="flex"
  justifyContent="flex-end"
  mb={1}
>
  <TablePagination
    component="div"
    count={rowsFiltrados.length}
    page={paginationModel.page}
    onPageChange={(_, newPage) =>
      setPaginationModel((prev) => ({
        ...prev,
        page: newPage,
      }))
    }
    rowsPerPage={paginationModel.pageSize}
    onRowsPerPageChange={(e) =>
      setPaginationModel({
        page: 0,
        pageSize: parseInt(e.target.value, 10),
      })
    }
    rowsPerPageOptions={[10, 25, 50, 100]}
  />
</Box>
      {/* 🔥 DATAGRID PRINCIPAL — con getRowClassName para pintar filas */}
      <DataGrid
        rows={rowsFiltrados}
        columns={columns}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowClassName={(params) =>
          params.row.color ? `row-color-${params.row.id}` : ""
        }
      />

      {/* 🔥 PINTADO DE FILAS POR COLOR */}
      <style>
        {rows
          .map(
            (row) => `
          .row-color-${row.id} {
            background-color: ${colorMap[row.color as number] || "transparent"} !important;
          }
        `
          )
          .join("\n")}
      </style>

      {/* Ancla para el scroll al final */}
      <div ref={bottomRef} />

      <ExpedienteDetalleModal
        open={openDetalle}
        expediente={selectedExpediente}
        onClose={() => setOpenDetalle(false)}
        onUpdate={handleUpdateRow}
      />

      {/* 🔥 BOTÓN FLOTANTE — ir al final de la página */}
      <Box
        onClick={() =>
          bottomRef.current?.scrollIntoView({ behavior: "smooth" })
        }
        title="Ir al final de la página"
        sx={{
          position: "fixed",
          bottom: 28,
          left: 28,
          width: 44,
          height: 44,
          borderRadius: "50%",
          backgroundColor: "primary.main",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: 4,
          zIndex: 1300,
          transition: "background-color 0.2s",
          "&:hover": { backgroundColor: "primary.dark" },
        }}
      >
        <KeyboardArrowDownIcon />
      </Box>
    </Box>
  );
};

/* 🔥 Helper círculo de color */
const circle = (color: string) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  backgroundColor: color,
  cursor: "pointer",
  border: "2px solid black",
});

export default DashboardPage;